import React from 'react';
import { Card, CardContent } from '../../../../components/card';

export interface EventPerformanceRow {
    id: string;
    eventName: string;
    date: string; // display e.g. "01/16/2025"
    rsvp: number;
    attended: number;
    showRatePercent: number; // 0-100, used for color (e.g. >= 85 green, else amber)
}

interface EventPerformanceTableProps {
    rows: EventPerformanceRow[];
}

/** Show rate color: green when >= 85%, amber otherwise (per Figma). */
const getShowRateColor = (percent: number): string => {
    return percent >= 85 ? '#059669' : '#D97706';
};

export const EventPerformanceTable: React.FC<EventPerformanceTableProps> = ({ rows }) => {
    return (
        <Card className="bg-white dark:bg-gray-800 rounded-[12.13px] border border-[#E6E6E6] dark:border-gray-700 p-[1.60px]">
            <CardContent className="p-0">
                <div className="pt-[19.50px] px-[19.50px] flex flex-col gap-[4.87px]">
                    <div className="text-[15px] font-semibold text-[#364153] dark:text-white leading-[13px]" style={{ fontFamily: 'Nunito' }}>
                        Event Performance Table
                    </div>
                    <div className="text-[13px] font-normal text-[#4A5565] dark:text-gray-400 leading-[19.50px]" style={{ fontFamily: 'Nunito' }}>
                        Individual event breakdown with attendance and conversion metric
                    </div>
                </div>

                <div className="flex flex-col gap-0 mt-[19.49px]">
                    {/* Table header */}
                    <div className="h-[32.50px] px-[15px] pb-[0.80px] bg-[#F9FAFB] dark:bg-gray-700 border-b border-[rgba(0,0,0,0.10)] dark:border-gray-600 flex items-center">
                        <div className="flex-1 min-w-0 px-[6.50px] text-[11.40px] font-semibold text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                            Event Name
                        </div>
                        <div className="w-[113.23px] shrink-0 px-[6.50px] text-center text-[11.40px] font-semibold text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                            Date
                        </div>
                        <div className="w-[113.23px] shrink-0 px-[6.50px] text-center text-[11.40px] font-semibold text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                            RSVP
                        </div>
                        <div className="w-[113.23px] shrink-0 px-[6.50px] text-center text-[11.40px] font-semibold text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                            Attended
                        </div>
                        <div className="w-[169.24px] shrink-0 px-[6.50px] text-center text-[11.40px] font-semibold text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                            Show rate
                        </div>
                    </div>

                    {/* Table body */}
                    <div className="flex flex-col">
                        {rows.map((row, idx) => (
                            <div
                                key={row.id}
                                className={`h-[39.80px] px-[15px] flex items-center ${idx < rows.length - 1 ? 'pb-[0.80px] border-b border-[rgba(0,0,0,0.10)] dark:border-gray-600' : ''}`}
                            >
                                <div className="flex-1 min-w-0 px-[6.50px] py-[10.88px] text-[11.40px] font-medium text-[#4A5565] dark:text-gray-300 leading-[16.25px] truncate" style={{ fontFamily: 'Nunito' }}>
                                    {row.eventName}
                                </div>
                                <div className="w-[113.23px] shrink-0 px-[6.50px] text-center text-[11.40px] font-medium text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                    {row.date}
                                </div>
                                <div className="w-[113.23px] shrink-0 px-[6.50px] text-center text-[11.40px] font-medium text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                    {row.rsvp}
                                </div>
                                <div className="w-[113.23px] shrink-0 px-[6.50px] text-center text-[11.40px] font-medium text-[#364153] dark:text-gray-200 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                    {row.attended}
                                </div>
                                <div className="w-[169.24px] shrink-0 px-[6.50px] flex justify-center items-center">
                                    <span
                                        className="text-[11.40px] font-medium leading-[16.25px]"
                                        style={{ color: getShowRateColor(row.showRatePercent), fontFamily: 'Nunito' }}
                                    >
                                        {row.showRatePercent.toFixed(1)}%
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
